import { makeAutoObservable, runInAction } from 'mobx'
import RecruitingMeetingService from './../../shared/services/RecruitingMeeting.service'

/**
 * Group Recruiting Meeting — page-level MobX store.
 *
 * Owns the 3-screen prospect/leader flow state, the verify/leader-session
 * response payloads, and the Zoom SDK readiness flag.
 *
 * View states:
 *   'verifying'   - Screen 1 input form (or leader-session probe in progress)
 *   'waiting'     - Screen 2 with countdown; Zoom SDK Connecting
 *   'live'        - Screen 2 with Zoom SDK Connected
 *   'enrolling'   - Screen 3 (desktop split or mobile new-tab)
 *   'ended'       - Meeting has ended
 *   'leader_idle' - Authed leader, but no current meeting (see leaderEmptyReason)
 */
class RecruitingMeetingStore {
  viewState = 'verifying'
  isLeaderProbe = true // true until we know if the visitor is an authed leader

  verifying = false
  identifierInput = ''
  errorMessage = null

  // Set when leader-session resolves to 404 for a recognized authed leader.
  // 'no_meeting_today' | 'meeting_completed' | 'meeting_upcoming' | null
  leaderEmptyReason = null

  // ISO-UTC start time when leaderEmptyReason === 'meeting_upcoming'.
  // Rendered in the viewer's local timezone by the page.
  leaderUpcomingStartsAt = null

  // Populated from /verify or /leader-session
  meetingContext = null

  // Internal flag toggled by MeetingRoom once Zoom SDK status === Connected
  zoomLive = false

  // Toggled by MeetingRoom once Zoom SDK status === Ended
  zoomEnded = false

  constructor() {
    makeAutoObservable(this)
  }

  setIdentifier = value => {
    this.identifierInput = value || ''
    this.errorMessage = null
  }

  /** Tell the store there's no point probing /leader-session (e.g., visitor
   *  is anonymous). Exposed as an action so the page component doesn't have
   *  to wrap a single observable mutation in runInAction. */
  skipLeaderProbe = () => {
    this.isLeaderProbe = false
  }

  /** Try leader-session for an authed leader. Falls through to identity
   *  verification when the user is anonymous, not a leader, or there's no
   *  meeting today. */
  tryLeaderSession = async () => {
    this.isLeaderProbe = true
    try {
      const data = await RecruitingMeetingService.fetchLeaderSession()
      runInAction(() => {
        this.meetingContext = { ...data, isLeader: true }
        this.viewState = 'live' // leaders skip the countdown UI; SDK handles state
      })
    } catch (ex) {
      // Distinguish three failure modes:
      //   - 404 with reason → visitor IS an authorized leader but there's
      //     no current meeting; show the leader-idle notice.
      //   - Anything else (401/403/other 404/network) → fall through to
      //     the prospect verify form, since we can't confirm leader status.
      const status = ex && ex.response && ex.response.status
      const data = (ex && ex.response && ex.response.data) || {}
      const reason = data.reason
      runInAction(() => {
        if (
          status === 404 &&
          (reason === 'no_meeting_today' || reason === 'meeting_completed' || reason === 'meeting_upcoming')
        ) {
          this.leaderEmptyReason = reason
          this.leaderUpcomingStartsAt = reason === 'meeting_upcoming' ? data.startsAt || null : null
          this.viewState = 'leader_idle'
        } else {
          this.leaderEmptyReason = null
          this.leaderUpcomingStartsAt = null
          this.viewState = 'verifying'
        }
      })
    } finally {
      runInAction(() => {
        this.isLeaderProbe = false
      })
    }
  }

  /** Verify a prospect by phone or email. */
  verifyIdentity = async () => {
    const id = (this.identifierInput || '').trim()
    if (!id) {
      this.errorMessage = 'Please enter your phone number or email.'
      return
    }
    this.verifying = true
    this.errorMessage = null
    try {
      const data = await RecruitingMeetingService.verifyIdentity(id)
      runInAction(() => {
        this.meetingContext = { ...data, isLeader: false }
        this.viewState = 'waiting'
      })
    } catch (ex) {
      runInAction(() => {
        const status = ex && ex.response && ex.response.status
        if (status === 404) {
          this.errorMessage =
            "We can't find your registration. Please text 'enroll' back to the messaging phone number to receive the link to sign up. Otherwise, contact your recruiter for further assistance."
        } else if (status === 429) {
          this.errorMessage = 'Too many attempts. Please wait a few minutes and try again.'
        } else {
          this.errorMessage = 'Something went wrong. Please try again in a moment, or contact your recruiter.'
        }
      })
    } finally {
      runInAction(() => {
        this.verifying = false
      })
    }
  }

  /** Called by MeetingRoom when the Zoom SDK reports status changes. */
  onZoomConnected = () => {
    this.zoomLive = true
    this.zoomEnded = false
    if (this.viewState === 'waiting') this.viewState = 'live'
  }

  onZoomEnded = () => {
    this.zoomLive = false
    this.zoomEnded = true
    this.viewState = 'ended'
  }

  /** Prospect clicked "Start My Opportunity". */
  startOpportunity = () => {
    this.viewState = 'enrolling'
  }

  /** Reset to verifying screen (e.g., if leader session fails after probe). */
  resetToVerifying = () => {
    this.viewState = 'verifying'
    this.meetingContext = null
    this.errorMessage = null
    this.leaderEmptyReason = null
    this.leaderUpcomingStartsAt = null
  }
}

const Store = new RecruitingMeetingStore()
export default Store
